import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Megaphone, Sparkles, ShieldCheck, ShieldAlert, Check, ArrowLeft, Radio, Ban,
  RotateCcw, Copy, PencilLine, CheckCircle2, Loader2, PauseCircle, XCircle, Circle,
} from 'lucide-react';
import { useRunEvents } from '../../hooks/useRunEvents';
import pipelineService, {
  MarketingBrief, PipelineRun, GuardrailReport, StageResult,
} from '../../services/pipelineService';
import { Agent } from '../../services/agentsService';
import { PageHeader, Spinner, StatusPill, Button } from '../../components/ui';
import EventTimeline from '../../components/ui/EventTimeline';
import AgentChatPanel from '../../components/AgentChatPanel';
import '../../styles/theme.css';

/**
 * Marketing agent workspace: submit a BRIEF, watch the shared pipeline run live (intake →
 * draft → guardrails → review → finalize), then review / edit / approve the deliverable.
 * Runs entirely on the shared engine (orchestrator + live SSE bus + gates) reused by the Doc
 * agent — only the input (a brief) and the results rendering are marketing-specific.
 */

// The marketing stages, in order, with plain-language labels for the stepper.
const PHASES: { key: string; label: string }[] = [
  { key: 'intake', label: 'Brief' },
  { key: 'draft', label: 'Writing' },
  { key: 'guardrail_check', label: 'Guardrails' },
  { key: 'human_review', label: 'Your review' },
  { key: 'finalize', label: 'Ready' },
];

function iconFor(status: string, size = 20) {
  switch (status) {
    case 'succeeded': return <CheckCircle2 size={size} color="var(--mx-success)" />;
    case 'running': return <Loader2 size={size} color="var(--mx-accent)" className="mx-spin" />;
    case 'paused': return <PauseCircle size={size} color="var(--mx-warning)" />;
    case 'failed': return <XCircle size={size} color="var(--mx-danger)" />;
    default: return <Circle size={size} color="var(--mx-border-strong)" />;
  }
}

const MarketingStepper: React.FC<{ stages: StageResult[] }> = ({ stages }) => {
  const statusOf = (name: string) => stages.find((s) => s.stage === name)?.status || 'pending';
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
      {PHASES.map((p, i) => {
        const status = statusOf(p.key);
        const done = status === 'succeeded';
        return (
          <React.Fragment key={p.key}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '0 0 auto', width: 104 }}>
              {iconFor(status)}
              <div style={{ fontSize: 11.5, fontWeight: 600, marginTop: 6, textAlign: 'center',
                color: status === 'pending' ? 'var(--mx-text-subtle)' : 'var(--mx-text)' }}>{p.label}</div>
            </div>
            {i < PHASES.length - 1 && (
              <div style={{ flex: 1, height: 2, marginTop: 10, borderRadius: 2,
                background: done ? 'var(--mx-success)' : 'var(--mx-border)' }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const CHANNELS = [['social', 'Social post'], ['blog', 'Blog'], ['email', 'Email'], ['ad', 'Ad copy']];
const PERSONAS = [['copywriter', 'Copywriter'], ['social_manager', 'Social manager'], ['brand_photographer', 'Brand / photo']];
const LENGTHS = [['short', 'Short'], ['medium', 'Medium'], ['long', 'Long']];

// ------------------------------------------------------------------ brief form
const BriefForm: React.FC<{ agent: Agent; onStarted: (runId: string) => void }> = ({ agent, onStarted }) => {
  const p = agent.params || {};
  const [brief, setBrief] = useState<MarketingBrief>({
    topic: '', goal: '', inputs: '',
    channel: p.channel || 'social', content_persona: p.content_persona || 'copywriter',
    tone: p.tone || '', brand_voice: p.brand_voice || '', target_audience: p.target_audience || '',
    length: p.length || 'short',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k: keyof MarketingBrief, v: string) => setBrief((b) => ({ ...b, [k]: v }));

  const submit = async () => {
    if (!brief.topic && !brief.goal) { setErr('Add a topic or a goal to get started.'); return; }
    setBusy(true); setErr('');
    try {
      const { run } = await pipelineService.startMarketingRun(brief);
      onStarted(run.id);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Could not start the run.');
    } finally { setBusy(false); }
  };

  const field = (label: string, node: React.ReactNode) => (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <span style={{ fontSize: 12.5, fontWeight: 600 }}>{label}</span>{node}
    </label>
  );
  const sel = (k: keyof MarketingBrief, opts: string[][]) => (
    <select className="mx-input" value={brief[k] as string} onChange={(e) => set(k, e.target.value)}>
      {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );

  return (
    <div className="mx-card mx-card-pad" style={{ maxWidth: 720, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, color: 'var(--mx-accent)' }}>
        <Sparkles size={17} /><span style={{ fontWeight: 600, color: 'var(--mx-text)' }}>Start from a brief</span>
      </div>
      {field('Topic / subject', <input className="mx-input" placeholder="e.g. Launch of our new cold-brew line"
        value={brief.topic} onChange={(e) => set('topic', e.target.value)} />)}
      {field('Goal', <input className="mx-input" placeholder="e.g. Drive pre-orders before Friday"
        value={brief.goal} onChange={(e) => set('goal', e.target.value)} />)}
      <div className="mx-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {field('Channel', sel('channel', CHANNELS))}
        {field('Persona', sel('content_persona', PERSONAS))}
        {field('Length', sel('length', LENGTHS))}
        {field('Tone', <input className="mx-input" placeholder="friendly, confident"
          value={brief.tone} onChange={(e) => set('tone', e.target.value)} />)}
      </div>
      {field('Target audience', <input className="mx-input" placeholder="e.g. busy urban professionals, 25-40"
        value={brief.target_audience} onChange={(e) => set('target_audience', e.target.value)} />)}
      {field('Brand voice / style notes', <input className="mx-input" placeholder="optional — how the brand sounds"
        value={brief.brand_voice} onChange={(e) => set('brand_voice', e.target.value)} />)}
      {field('Key points to include', <textarea className="mx-input" rows={3} placeholder="optional — facts, offers, links"
        value={brief.inputs} onChange={(e) => set('inputs', e.target.value)} />)}
      {err && <div style={{ color: 'var(--mx-danger)', fontSize: 12.5 }}>{err}</div>}
      <div>
        <Button icon={Megaphone} variant="primary" disabled={busy} onClick={submit}>
          {busy ? 'Starting…' : 'Generate content'}</Button>
      </div>
    </div>
  );
};

// ------------------------------------------------------------------ guardrail report
const GuardrailPanel: React.FC<{ report?: GuardrailReport }> = ({ report }) => {
  if (!report || !report.checks?.length) return null;
  const ok = report.passed;
  return (
    <div className="mx-card" style={{ borderColor: ok ? 'var(--mx-success)' : 'var(--mx-warning)' }}>
      <div className="mx-card-head" style={{ background: ok ? 'var(--mx-success-weak)' : 'var(--mx-warning-weak)' }}>
        <h3 className="mx-card-title" style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          {ok ? <ShieldCheck size={16} color="var(--mx-success)" /> : <ShieldAlert size={16} color="var(--mx-warning)" />}
          Guardrail check</h3>
        <span className="mx-subtle" style={{ fontSize: 12 }}>
          {ok ? 'All clear' : `${report.violations} to review`}</span>
      </div>
      <div className="mx-card-pad" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {report.checks.map((c) => (
          <div key={c.key} style={{ display: 'flex', gap: 9, alignItems: 'flex-start' }}>
            {c.passed
              ? <Check size={15} style={{ color: 'var(--mx-success)', flex: 'none', marginTop: 1 }} />
              : (c.severity === 'violation'
                ? <ShieldAlert size={15} style={{ color: 'var(--mx-danger)', flex: 'none', marginTop: 1 }} />
                : <ShieldAlert size={15} style={{ color: 'var(--mx-warning)', flex: 'none', marginTop: 1 }} />)}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{c.name}
                {!c.passed && c.severity === 'violation' && <span style={{ color: 'var(--mx-danger)', fontSize: 11, marginLeft: 6 }}>must review</span>}
                {!c.passed && c.severity === 'advisory' && <span style={{ color: 'var(--mx-warning)', fontSize: 11, marginLeft: 6 }}>advisory</span>}
              </div>
              <div className="mx-muted" style={{ fontSize: 12.5 }}>{c.detail}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ------------------------------------------------------------------ live run view
const RunView: React.FC<{ runId: string; onBack: () => void }> = ({ runId, onBack }) => {
  const { run, feed, connected, error, reload } = useRunEvents(runId);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState('');
  const [edited, setEdited] = useState(false);

  const gate = run?.hitl_gate?.gate;
  const atReview = gate === 'human_review' && run?.status === 'paused';
  const report: GuardrailReport | undefined =
    run?.hitl_gate?.payload?.guardrail_report || run?.result?.guardrail_report;
  const gateContent: string = run?.hitl_gate?.payload?.content || run?.result?.content || '';

  useEffect(() => { if (atReview && !edited) setDraft(gateContent); }, [atReview, gateContent, edited]);

  const activeRun = run && ['running', 'paused', 'cancelling'].includes(run.status);
  const terminal = run && ['succeeded', 'failed', 'cancelled'].includes(run.status);

  const act = async (label: string, fn: () => Promise<any>) => {
    setBusy(label);
    try { await fn(); await reload(); } finally { setBusy(''); }
  };
  const approve = (useEdit: boolean) => act('approve', () =>
    pipelineService.resumeMarketingRun(runId, useEdit ? draft : undefined, true));

  if (!run) {
    return <div className="mx-card mx-card-pad">{error ? <span className="mx-muted">{error}</span> : <Spinner label="Loading…" />}</div>;
  }

  const finalContent = run.result?.content || gateContent;
  const brief = run.brief || {};
  const title = brief.topic || brief.goal || 'Marketing content';

  return (
    <div>
      <Button icon={ArrowLeft} variant="ghost" size="sm" style={{ marginBottom: 12, paddingLeft: 4 }}
        onClick={onBack}>New brief</Button>
      <PageHeader title={title}
        subtitle={run.status === 'succeeded' ? 'Content finalized'
          : atReview ? 'Review and approve your content'
          : 'Creating your content…'}
        actions={<div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <StatusPill status={run.status} pulse />
          {connected && <span className="mx-chip" style={{ color: 'var(--mx-success)' }}>
            <Radio size={12} className="mx-pulsing" /> live</span>}
          {activeRun && <Button icon={Ban} variant="danger" size="sm" disabled={!!busy}
            onClick={() => act('cancel', () => pipelineService.cancelRun(run.id))}>Cancel</Button>}
          {terminal && <Button icon={RotateCcw} variant="secondary" size="sm" disabled={!!busy}
            onClick={onBack}>Start another</Button>}
        </div>} />

      <div className="mx-card mx-card-pad" style={{ marginBottom: 16 }}>
        <MarketingStepper stages={run.stages} />
      </div>

      <div className="mx-grid" style={{ gridTemplateColumns: '1.5fr 1fr', alignItems: 'start', gap: 16 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* content: editable at the review gate, read-only otherwise */}
          {(atReview || finalContent) && (
            <div className="mx-card">
              <div className="mx-card-head">
                <h3 className="mx-card-title" style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <Sparkles size={15} /> {run.status === 'succeeded' ? 'Final content' : 'Draft'}
                  {run.result?.degraded && <span className="mx-subtle" style={{ fontSize: 11 }}>· placeholder (no LLM configured)</span>}
                </h3>
                {run.status === 'succeeded' && (
                  <Button icon={Copy} variant="ghost" size="sm"
                    onClick={() => navigator.clipboard?.writeText(finalContent)}>Copy</Button>
                )}
              </div>
              <div className="mx-card-pad">
                {atReview ? (
                  <textarea className="mx-input" style={{ width: '100%', minHeight: 220, fontFamily: 'inherit', lineHeight: 1.5 }}
                    value={draft} onChange={(e) => { setDraft(e.target.value); setEdited(true); }} />
                ) : (
                  <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.55 }}>{finalContent}</div>
                )}
              </div>
              {atReview && (
                <div className="mx-card-pad" style={{ borderTop: '1px solid var(--mx-border)', display: 'flex', gap: 10, alignItems: 'center' }}>
                  <Button icon={Check} variant="primary" disabled={!!busy}
                    onClick={() => approve(edited)}>
                    {busy ? 'Finishing…' : edited ? 'Approve edited' : 'Approve & finish'}</Button>
                  {edited && <span className="mx-subtle" style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <PencilLine size={12} /> your edits will be saved</span>}
                </div>
              )}
            </div>
          )}
          {run.status === 'succeeded' && (
            <div className="mx-card" style={{ padding: '12px 15px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <Check size={18} style={{ color: 'var(--mx-success)', flex: 'none' }} />
              <div style={{ fontSize: 13 }}>
                <b>Approved &amp; finalized.</b>{run.result?.edited ? ' Saved with your edits.' : ''} Ready to publish.</div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <GuardrailPanel report={report} />
          <AgentChatPanel agentKey="marketing" runId={runId} />
          <div className="mx-card">
            <div className="mx-card-head"><h3 className="mx-card-title">Progress</h3></div>
            <div className="mx-card-pad" style={{ maxHeight: 320, overflowY: 'auto' }}>
              <EventTimeline feed={feed} live={!!connected} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ------------------------------------------------------------------ entry
const MarketingWorkspace: React.FC<{ agent: Agent }> = ({ agent }) => {
  const [runId, setRunId] = useState<string | null>(null);
  const [recent, setRecent] = useState<PipelineRun[]>([]);

  const loadRecent = useCallback(() => {
    pipelineService.listRuns(1, 5, undefined, 'marketing')
      .then((r) => setRecent(r.runs)).catch(() => setRecent([]));
  }, []);
  useEffect(() => { if (!runId) loadRecent(); }, [runId, loadRecent]);

  const started = useMemo(() => (id: string) => setRunId(id), []);

  if (runId) return <RunView runId={runId} onBack={() => setRunId(null)} />;

  return (
    <div>
      <PageHeader title={agent.name} subtitle={agent.persona || agent.description} />
      <div className="mx-grid" style={{ gridTemplateColumns: 'minmax(0, 720px) 1fr', gap: 16, alignItems: 'start' }}>
        <BriefForm agent={agent} onStarted={started} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <AgentChatPanel agentKey="marketing" />
          {recent.length > 0 && (
          <div className="mx-card">
            <div className="mx-card-head"><h3 className="mx-card-title">Recent</h3></div>
            <div className="mx-card-pad" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {recent.map((r) => (
                <button key={r.id} className="mx-card mx-card-pad" style={{ textAlign: 'left', cursor: 'pointer', border: '1px solid var(--mx-border)' }}
                  onClick={() => setRunId(r.id)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 600, fontSize: 13, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.brief?.topic || r.brief?.goal || 'Untitled brief'}</span>
                    <StatusPill status={r.status} />
                  </div>
                  <div className="mx-subtle" style={{ fontSize: 11.5, marginTop: 3 }}>
                    {r.brief?.channel || 'social'} · {r.updated_at ? new Date(r.updated_at).toLocaleString() : ''}</div>
                </button>
              ))}
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MarketingWorkspace;
